<template>
  <v-card
    id="enrollment"
    :border="$role == 'specialist'"
    flat
    tile
  >
    <v-card-title class="tab-title d-flex">
      <v-btn
        v-if="showProviderProfileNavBtn"
        @click="emit('toggleProviderProfileNav')"
        :aria-label="$bct('Toggle provider profile navigation')"
        data-cy="provider_profile_nav_icon"
        icon="menu_open"
        variant="text"
      />
      <h2 class="v-card-title py-0 px-0 d-flex align-center">
        {{ $bct('Enrollment') }}
      </h2>
    </v-card-title>
    <v-divider />
    <v-card-text class="my-4">
      <p class="mb-4">
        {{ $bct(description || 'How many children are currently enrolled in each age group?') }}
      </p>
      <div class="mt-0 d-none d-md-flex">
        <div class="d-inline-block w-250" />
        <div class="d-inline-block w-150 ta-center">
          {{ $bct('# of enrolled') }}
        </div>
      </div>
      <div
        v-for="program in programs"
        :key="program.id"
        class="mt-4"
      >
        <div class="d-inline-block w-250 h-56 va-top pt-4">
          {{ $bct(program.name) }}
        </div>
        <div class="d-inline-block w-150">
          <!-- eslint-disable-next-line prefer-standard-components/no-vuetify-inputs -- TODO migrate to LabeledTextfield -->
          <v-text-field
            v-model="program.enrolled_count"
            @change="changed = true"
            :aria-label="$bct('number')"
            min="0"
            type="number"
            variant="filled"
            hide-details
          />
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script setup>
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';

const props = defineProps({
  description: {
    type: String,
    default: null,
  },
  providerId: {
    type: String,
    default: null,
  },
  showProviderProfileNavBtn: {
    type: Boolean,
    default: null,
  },
});

const { api } = useApi();
const eventBus = useEventBus();
const $bct = inject('translator');

const emit = defineEmits(['change', 'progress', 'toggleProviderProfileNav']);

const changed = ref(false);
const isLoading = ref(false);
const programs = ref([]);

const progress = computed(() =>
  programs.value.every((program) => program.enrolled_count != null) ? 100 : 0,
);

watch(changed, (newValue) => {
  if (newValue) emit('change');
});

watch(programs, () => emit('progress', progress.value), { deep: true });

watch(
  () => props.providerId,
  async () => {
    await load();
  },
  { immediate: true },
);

async function load() {
  isLoading.value = true;
  const { data } = await api.program.index(props.providerId);

  programs.value = data;
  await nextTick();
  changed.value = false;
  isLoading.value = false;
}

async function save() {
  if (!changed.value) return;

  const results = await Promise.all(
    programs.value.map((program) =>
      api.program.update(props.providerId, program.id, {
        enrolled_count: program.enrolled_count,
      }),
    ),
  );

  const allSucceeded = results.every((response) => response?.status === 200);

  if (allSucceeded) {
    changed.value = false;
    eventBus.chime('Enrollment updated');
  } else {
    eventBus.chime('Some enrollment counts failed to save. Please try again.');
  }
}

// While in a shared component state for saveBar (specialist) provider editing flow,
// and the back/next (manager/provider) flow we have to expose both load and save for
// ProviderShow access
defineExpose({ load, save });
</script>
